import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, type Swiper as SwiperType } from 'swiper';
import 'swiper/swiper-bundle.css';
import 'swiper/css';
import {
  LeftSwipperArrows,
  RightSwipperArrows
} from '../../shared-components/SwipperArrows/SwipperArrows';
import { Attraction, Product } from '../../types/pages/tour';
import { optimizeImageUrl } from '../../utils/videoLinkOptimize';

export const WhatYouWillSeeSection = ({ product }: { product: Product }) => {
  const [isLastSlide, setIsLastSlide] = useState(false);
  const [isFirstSlide, setIsFirstSlide] = useState(true);
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const prevRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsFirstSlide(true);
    setIsLastSlide(false);

    if (swiperInstance && typeof swiperInstance.slideTo === 'function') {
      setTimeout(() => {
        try {
          swiperInstance.slideTo(0, 0);
        } catch (error) {
          console.warn('Failed to reset slider position:', error);
        }
      }, 0);
    }
  }, [product]);

  const onBeforeInit = (Swiper: SwiperType): void => {
    if (typeof Swiper.params.navigation !== 'boolean') {
      const navigation = Swiper.params.navigation;
      if (navigation) {
        navigation.prevEl = prevRef.current;
        navigation.nextEl = nextRef.current;
      }
    }
  };

  const handleSlideChange = (swiper: SwiperType) => {
    setIsFirstSlide(swiper.isBeginning);
    setIsLastSlide(swiper.isEnd);
  };

  return (
    <div className="flex flex-col justify-between w-full max-w-[100vw]">
      <div className="flex py-4 justify-between items-center">
        <h2
          className="text-lg lg:text-xl font-semibold text-start"
          id="what-you-will-see"
        >
          What you&apos;ll see{' '}
        </h2>
        {product?.attractions?.length > 1 && (
          <div id="slider-btn" className="flex gap-2 items-center">
            <div ref={prevRef}>
              <LeftSwipperArrows isFirstSlide={isFirstSlide} />
            </div>
            <div ref={nextRef}>
              <RightSwipperArrows isLastSlide={isLastSlide} />
            </div>
          </div>
        )}
      </div>

      <Swiper
        modules={[Navigation]}
        className="relative w-full"
        spaceBetween={20}
        slidesPerView={1}
        onSlideChange={handleSlideChange}
        onBeforeInit={onBeforeInit}
        onSwiper={setSwiperInstance}
        navigation={{
          prevEl: prevRef?.current,
          nextEl: nextRef?.current
        }}
        grabCursor
        breakpoints={{
          300: { slidesPerView: 1.2 },
          600: { slidesPerView: 2 },
          800: { slidesPerView: 1.8 },
          1024: { slidesPerView: 2 },
          1200: { slidesPerView: 2.5 },
          1600: { slidesPerView: 3 }
        }}
      >
        {product.attractions.length > 0 ? (
          product?.attractions?.map((attraction: Attraction) => {
            const { alt, url } = attraction?.heroMedia[0] || {};
            return (
              <SwiperSlide key={attraction.name}>
                <Link href={`/${attraction.slug}`} target="_blank">
                  <div className="bg-white shadow-sm overflow-hidden h-[360px] max-h-[400px]">
                    <div className="relative w-full h-[235px]">
                      <Image
                        src={optimizeImageUrl(url)}
                        alt={alt || `${attraction.name} in 2024`}
                        layout="fill"
                        objectFit="cover"
                        className="rounded-t-lg"
                        loading="lazy"
                      />
                    </div>
                    <div className="py-4">
                      <h3 className="text-base sm:text-lg font-semibold text-[#333333] mb-2 whitespace-nowrap overflow-hidden text-ellipsis">
                        {attraction.name}
                      </h3>
                      <p className="text-sm md:text-base text-black13">
                        {attraction?.cardSnippet?.length > 100
                          ? `${attraction.cardSnippet.slice(0, 100)}...`
                          : attraction.cardSnippet}
                      </p>
                    </div>
                  </div>
                </Link>
              </SwiperSlide>
            );
          })
        ) : (
          <div className="flex justify-start items-center h-12 text-[14px] md:text-base">
            No Attractions to show
          </div>
        )}
      </Swiper>
    </div>
  );
};

export default WhatYouWillSeeSection;
